import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { 
  FileText, Download, Eye, Briefcase, Upload, Trash2,
  Phone, Star, Clock, Users, RefreshCw,
  Copy, Check, Settings, Link as LinkIcon, File
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';

const CMSBrochurePage = () => {
  const [copied, setCopied] = useState(false);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // URLs
  const publicBrochureUrl = `${window.location.origin}/site/brochure`;
  const autoDownloadUrl = `${process.env.REACT_APP_BACKEND_URL}/api/website/brochure/download`;

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await api.get('/cms/brochure-config');
      setConfig(res.data || { brochure_mode: 'auto' });
    } catch (error) {
      setConfig({ brochure_mode: 'auto' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/cms/brochure-config', config);
      toast.success('Configuration sauvegardée !');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Veuillez sélectionner un fichier PDF');
      return;
    }

    if (file.size > 20 * 1024 * 1024) { // 20MB max
      toast.error('Le fichier est trop volumineux (max 20 Mo)');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await api.post('/cms/brochure/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setConfig(prev => ({
        ...prev,
        custom_pdf_url: res.data.url,
        custom_pdf_name: file.name
      }));

      toast.success('PDF uploadé avec succès !');
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const removeCustomPdf = () => {
    setConfig(prev => ({
      ...prev,
      custom_pdf_url: null,
      custom_pdf_name: null
    }));
  };

  const downloadBrochure = () => {
    if (config?.brochure_mode === 'custom' && config?.custom_pdf_url) {
      window.open(config.custom_pdf_url, '_blank');
    } else {
      window.open(autoDownloadUrl, '_blank');
    }
  };

  const previewBrochure = () => {
    window.open('/site/brochure', '_blank');
  };

  const copyLink = async () => {
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(publicBrochureUrl);
      } else {
        // Fallback for older browsers/mobile
        const textArea = document.createElement('textarea');
        textArea.value = publicBrochureUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        textArea.style.top = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          document.execCommand('copy');
        } catch (err) {
          console.error('Fallback copy failed:', err);
          throw new Error('Copy failed');
        }
        
        document.body.removeChild(textArea);
      }
      
      setCopied(true);
      toast.success('Lien copié !');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy error:', err);
      // Show the link in a prompt as last resort
      window.prompt('Copiez ce lien :', publicBrochureUrl);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Brochure PDF</h1>
          <p className="text-slate-500">
            Gérez et partagez votre brochure commerciale
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={previewBrochure}>
            <Eye className="w-4 h-4 mr-2" /> Aperçu
          </Button>
          <Button onClick={downloadBrochure} className="bg-gradient-to-r from-cyan-500 to-blue-600">
            <Download className="w-4 h-4 mr-2" /> Télécharger
          </Button>
          <Button onClick={handleSave} disabled={saving} variant="outline">
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
            Sauvegarder
          </Button>
        </div>
      </div>

      {/* Share Link Card */}
      <Card className="border-2 border-green-300 bg-green-50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <LinkIcon className="w-5 h-5 text-green-600" />
            Lien de partage
          </CardTitle>
          <CardDescription>
            Partagez ce lien avec vos clients et prospects
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 bg-white rounded-lg border border-green-200 p-3">
              <code className="text-sm text-slate-700 break-all">{publicBrochureUrl}</code>
            </div>
            <Button 
              onClick={copyLink}
              className={copied ? "bg-green-600 hover:bg-green-700" : "bg-green-500 hover:bg-green-600"}
            >
              {copied ? <><Check className="w-4 h-4 mr-2" /> Copié !</> : <><Copy className="w-4 h-4 mr-2" /> Copier</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Brochure Mode Selection */}
      <Card className="border-2 border-cyan-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-cyan-500" />
            Type de brochure
          </CardTitle>
          <CardDescription>
            Choisissez entre une brochure auto-générée ou un PDF personnalisé
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={config?.brochure_mode || 'auto'}
            onValueChange={(value) => setConfig(prev => ({ ...prev, brochure_mode: value }))}
            className="space-y-4"
          >
            {/* Option 1: Auto-generated */}
            <div className={`flex items-start gap-4 p-4 rounded-xl border-2 transition-all ${
              config?.brochure_mode === 'auto' ? 'border-cyan-500 bg-cyan-50' : 'border-slate-200 hover:border-slate-300'
            }`}>
              <RadioGroupItem value="auto" id="auto" className="mt-1" />
              <div className="flex-1">
                <Label htmlFor="auto" className="text-base font-semibold cursor-pointer flex items-center gap-2">
                  <RefreshCw className="w-5 h-5 text-cyan-500" />
                  Brochure auto-générée
                  <Badge className="bg-cyan-100 text-cyan-700">Recommandé</Badge>
                </Label>
                <p className="text-sm text-slate-500 mt-1">
                  Générée automatiquement à partir des données du site (équipe, réalisations, services, tarifs...)
                </p>
                {config?.brochure_mode === 'auto' && (
                  <div className="mt-3 flex gap-2">
                    <Link to="/cms/brochure-config">
                      <Button size="sm" variant="outline">
                        <Settings className="w-4 h-4 mr-2" /> Configurer le contenu
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            </div>

            {/* Option 2: Custom PDF */}
            <div className={`flex items-start gap-4 p-4 rounded-xl border-2 transition-all ${
              config?.brochure_mode === 'custom' ? 'border-orange-500 bg-orange-50' : 'border-slate-200 hover:border-slate-300'
            }`}>
              <RadioGroupItem value="custom" id="custom" className="mt-1" />
              <div className="flex-1">
                <Label htmlFor="custom" className="text-base font-semibold cursor-pointer flex items-center gap-2">
                  <Upload className="w-5 h-5 text-orange-500" />
                  PDF personnalisé
                </Label>
                <p className="text-sm text-slate-500 mt-1">
                  Uploadez votre propre brochure PDF (créée par un graphiste par exemple)
                </p>
                
                {config?.brochure_mode === 'custom' && (
                  <div className="mt-4 space-y-3">
                    {config?.custom_pdf_url ? (
                      <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-orange-200">
                        <File className="w-8 h-8 text-orange-500" />
                        <div className="flex-1">
                          <p className="font-medium text-slate-800">{config.custom_pdf_name || 'brochure.pdf'}</p>
                          <p className="text-xs text-slate-500">PDF uploadé</p>
                        </div>
                        <Button size="sm" variant="ghost" onClick={removeCustomPdf}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => window.open(config.custom_pdf_url, '_blank')}>
                          <Eye className="w-4 h-4 mr-1" /> Voir
                        </Button>
                      </div>
                    ) : (
                      <div 
                        className="border-2 border-dashed border-orange-300 rounded-xl p-6 text-center cursor-pointer hover:bg-orange-50 transition-colors"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <input 
                          type="file" 
                          ref={fileInputRef}
                          className="hidden" 
                          accept=".pdf"
                          onChange={handleFileUpload}
                        />
                        {uploading ? (
                          <RefreshCw className="w-10 h-10 mx-auto text-orange-400 animate-spin" />
                        ) : (
                          <Upload className="w-10 h-10 mx-auto text-orange-400" />
                        )}
                        <p className="mt-2 font-medium text-slate-700">
                          {uploading ? 'Upload en cours...' : 'Cliquez pour uploader un PDF'}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">Maximum 20 Mo</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Info Cards - Only show for auto mode */}
      {config?.brochure_mode === 'auto' && (
        <div className="grid md:grid-cols-3 gap-4">
          <Card className="border-l-4 border-l-cyan-500">
            <CardContent className="pt-4">
              <h3 className="font-semibold flex items-center gap-2 mb-2">
                <FileText className="w-4 h-4 text-cyan-500" />
                Pages 1-5
              </h3>
              <p className="text-sm text-slate-500">Couverture, Services & Tarifs (Graphisme, Vidéo, Web, Marketing)</p>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-violet-500">
            <CardContent className="pt-4">
              <h3 className="font-semibold flex items-center gap-2 mb-2">
                <Users className="w-4 h-4 text-violet-500" />
                Pages 6-8
              </h3>
              <p className="text-sm text-slate-500">Équipe, Timeline et Réalisations avec images</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500">
            <CardContent className="pt-4">
              <h3 className="font-semibold flex items-center gap-2 mb-2">
                <Phone className="w-4 h-4 text-green-500" />
                Pages 9-10
              </h3>
              <p className="text-sm text-slate-500">Partenaires, Témoignages et Coordonnées</p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default CMSBrochurePage;
